"use client";

import { useState } from "react";
import Link from "@/components/locale-link";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { AreaDiagram } from "@/components/tools/geometry/area-diagrams";
import {
  DiagramDownload,
  useDiagramRef,
} from "@/components/tools/geometry/diagram";
import {
  type DecimalPlaces,
  type Format,
  formatMeasure,
  formatTableValue,
} from "@/components/tools/geometry/format";
import {
  MeasurementFields,
  UnitControls,
} from "@/components/tools/geometry/measurement-fields";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  type AngleInputUnit,
  type ParsedAngle,
  parseAngleInput,
} from "@/lib/tool-kit/geometry/angle-input";
import {
  AREA_SHAPE_FIELDS,
  AREA_SHAPE_IDS,
  type AreaFailure,
  type AreaResult,
  type AreaShapeId,
  computeArea,
  type LengthFieldId,
  type Metric,
} from "@/lib/tool-kit/geometry/area";
import {
  type Exact,
  type ExactFormat,
  formatExact,
  formatExactWithUnit,
  isRationalExact,
} from "@/lib/tool-kit/geometry/exact";
import {
  answerUnit,
  readMeasurements,
  styleOfTexts,
  type UnitSetup,
} from "@/lib/tool-kit/geometry/measurements";
import {
  conversionRows,
  type LengthUnitId,
  unitSymbol,
} from "@/lib/tool-kit/geometry/units";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import { formatStyled, needsFraction } from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const AREA_CALCULATOR_STRINGS = {
  shapeLabel: "Shape",
  shapes: {
    square: "Square",
    rectangle: "Rectangle",
    triangle: "Triangle (base and height)",
    triangle3: "Triangle (three sides, Heron's formula)",
    parallelogram: "Parallelogram",
    trapezoid: "Trapezoid",
    rhombus: "Rhombus (from its diagonals)",
    polygon: "Regular polygon",
    ellipse: "Ellipse",
    annulus: "Ring (annulus)",
    sector: "Circular sector",
  },
  fields: {
    side: "Side length (s)",
    length: "Length (l)",
    width: "Width (w)",
    base: "Base (b)",
    height: "Height (h)",
    sideA: "Side a",
    sideB: "Side b",
    sideC: "Side c",
    base1: "Parallel side a",
    base2: "Parallel side b",
    diag1: "Diagonal d₁",
    diag2: "Diagonal d₂",
    semiA: "Semi-axis a (half the width)",
    semiB: "Semi-axis b (half the height)",
    outer: "Outer radius (R)",
    inner: "Inner radius (r)",
    radius: "Radius (r)",
  },
  sidesLabel: "Number of sides (n)",
  angleLabel: "Central angle (θ)",
  angleUnitLabel: "Angle unit",
  angleUnits: { deg: "Degrees", rad: "Radians" },
  angleHint:
    "In radians, type a multiple of π such as pi/3 or 2π/3 to keep the answer exact.",
  unitLabel: "Unit of length",
  mixLabel: "Measurements are in different units",
  resultUnitLabel: "Give the answer in",
  decimalsLabel: "Decimal places",
  unitAria: "{field}: unit",
  lengthUnits: {
    mm: "Millimeters (mm)",
    cm: "Centimeters (cm)",
    m: "Meters (m)",
    km: "Kilometers (km)",
    in: "Inches (in)",
    ft: "Feet (ft)",
    yd: "Yards (yd)",
    mi: "Miles (mi)",
  },
  circleNote: "Need a whole circle — its area, circumference or a segment?",
  circleLink: "Use the Circle Calculator",
  results: "Results",
  area: "Area",
  areaDecimal: "Area as a decimal",
  moreHeading: "Other measurements",
  measurementHeader: "Measurement",
  exactHeader: "Exact",
  decimalHeader: "Decimal",
  approximate: "approximate",
  metrics: {
    perimeter: "Perimeter",
    diagonal: "Diagonal",
    semiperimeter: "Semi-perimeter (s)",
    midsegment: "Midsegment",
    sideLength: "Side length",
    apothem: "Apothem",
    circumradius: "Circumradius",
    interiorAngle: "Each interior angle",
    centralAngle: "Central angle",
    perimeterApprox: "Perimeter (Ramanujan's approximation)",
    width: "Ring width",
    arcLength: "Arc length",
    angleDegrees: "Central angle in degrees",
  },
  workingHeading: "The working",
  figureHeading: "Figure",
  figureTitle: "{shape} with its measurements labeled",
  downloadFigure: "Download figure (PNG)",
  unitsHeading: "The same area in other units",
  unitHeader: "Unit",
  areaHeader: "Area",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "{shape}: area = {exact} ≈ {decimal}",
  summaryExact: "{shape}: area = {exact}",
  enter: "Enter every measurement to see the area.",
  invalid:
    "Enter each measurement as a number — decimals and fractions such as 3/4 work.",
  invalidAngle:
    "Enter the angle as a number; in radians you can also write multiples of π, such as pi/3.",
  positive: "Every measurement must be greater than zero.",
  triangle:
    "These three sides cannot make a triangle: the longest side must be shorter than the other two added together.",
  sides: "Enter a whole number of sides from 3 to 1000.",
  angle: "Enter an angle greater than 0° and at most 360° (2π radians).",
  annulus: "The outer radius must be larger than the inner radius.",
} as const;

type Strings = typeof AREA_CALCULATOR_STRINGS;

const DEFAULT_VALUES: Record<LengthFieldId, string> = {
  side: "5",
  length: "8",
  width: "5",
  base: "6",
  height: "4",
  sideA: "4",
  sideB: "5",
  sideC: "6",
  base1: "3",
  base2: "5",
  diag1: "6",
  diag2: "8",
  semiA: "5",
  semiB: "3",
  outer: "5",
  inner: "3",
  radius: "3",
};

const ID_PREFIX = "area";

/** The exact form with its unit, or the decimal when there is no exact form. */
function exactOrDecimal(
  exact: Exact | null,
  decimal: number,
  unit: string,
  fmt: Format,
  places: number,
  format: ExactFormat
): string {
  return exact
    ? formatExactWithUnit(exact, unit, format)
    : `${formatMeasure(fmt, decimal, places)} ${unit}`;
}

function needsDecimal(exact: Exact | null, format: ExactFormat): boolean {
  if (exact === null) {
    return false;
  }
  if (!isRationalExact(exact)) {
    return true;
  }
  return exact.some((term) =>
    format.decimals ? needsFraction(term.coef) : term.coef.den !== BigInt(1)
  );
}

function metricUnit(metric: Metric, lengthUnit: string): string {
  return metric.kind === "angle" ? "°" : ` ${lengthUnit}`;
}

function MetricsTable({
  metrics,
  lengthUnit,
  strings,
  fmt,
  places,
  format,
}: {
  metrics: Metric[];
  lengthUnit: string;
  strings: Strings;
  fmt: Format;
  places: number;
  format: ExactFormat;
}) {
  return (
    <div className={`${CARD} overflow-x-auto`}>
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.measurementHeader}
            </th>
            <th className={TH} scope="col">
              {strings.exactHeader}
            </th>
            <th className={TH} scope="col">
              {strings.decimalHeader}
            </th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((metric) => {
            const suffix = metricUnit(metric, lengthUnit);
            const exact = metric.exact
              ? formatExact(metric.exact, format)
              : null;
            return (
              <tr key={metric.key}>
                <th className={`${TD} font-normal`} scope="row">
                  {strings.metrics[metric.key]}
                </th>
                <td className="border-border border-b px-3 py-2 font-mono text-foreground">
                  {exact === null ? strings.approximate : `${exact}${suffix}`}
                </td>
                <td className="border-border border-b px-3 py-2 font-mono text-foreground">
                  {formatMeasure(fmt, metric.value, places)}
                  {suffix}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Results({
  shape,
  result,
  strings,
  setup,
  places,
  figure,
  format,
}: {
  shape: AreaShapeId;
  result: AreaResult;
  strings: Strings;
  setup: UnitSetup;
  places: number;
  figure: React.ReactNode;
  format: ExactFormat;
}) {
  const fmt = useToolNumber();
  const unit = answerUnit(setup);
  const areaUnit = unitSymbol(unit, 2);
  const headline = exactOrDecimal(
    result.area,
    result.areaValue,
    areaUnit,
    fmt,
    places,
    format
  );
  const decimal = `${formatMeasure(fmt, result.areaValue, places)} ${areaUnit}`;
  const showDecimal = needsDecimal(result.area, format);
  const summaryKey = showDecimal ? strings.summary : strings.summaryExact;
  const summary = t(summaryKey, {
    shape: strings.shapes[shape],
    exact: headline,
    decimal,
  });
  const rows = conversionRows(result.areaValue, unit, 2).map((row) => [
    row.symbol,
    formatTableValue(fmt, row.value),
  ]);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.area} value={headline} />
        {showDecimal ? (
          <StatCard label={strings.areaDecimal} value={decimal} />
        ) : null}
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
      {result.metrics.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.moreHeading}</h3>
          <MetricsTable
            fmt={fmt}
            format={format}
            lengthUnit={unitSymbol(unit, 1)}
            metrics={result.metrics}
            places={places}
            strings={strings}
          />
        </>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p>{result.formula}</p>
        {result.steps.map((step) => (
          <p key={step}>{step}</p>
        ))}
      </div>
      <h3 className={WIDGET_H3}>{strings.figureHeading}</h3>
      {figure}
      <h3 className={WIDGET_H3}>{strings.unitsHeading}</h3>
      <DataTable head={[strings.unitHeader, strings.areaHeader]} rows={rows} />
    </>
  );
}

function errorText(strings: Strings, error: AreaFailure["error"]): string {
  const messages: Record<AreaFailure["error"], string> = {
    positive: strings.positive,
    triangle: strings.triangle,
    sides: strings.sides,
    angle: strings.angle,
    annulus: strings.annulus,
  };
  return messages[error];
}

interface OutcomeProps {
  shape: AreaShapeId;
  values: Record<string, string>;
  sidesText: string;
  angleText: string;
  angleUnit: AngleInputUnit;
  setup: UnitSetup;
  places: number;
  strings: Strings;
}

function Outcome({
  shape,
  values,
  sidesText,
  angleText,
  angleUnit,
  setup,
  places,
  strings,
}: OutcomeProps) {
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const svgRef = useDiagramRef();
  const fields = AREA_SHAPE_FIELDS[shape];
  const style = styleOfTexts(fields.map((id) => values[id] ?? ""));
  const read = readMeasurements(fields, values, setup, mark, style);
  if (!read.ok) {
    return read.error === "enter" ? (
      <Notice>{strings.enter}</Notice>
    ) : (
      <ErrorNotice>{strings.invalid}</ErrorNotice>
    );
  }
  const sides = parseIntegerInput(sidesText);
  const angle: ParsedAngle | null = parseAngleInput(angleText, angleUnit);
  if (shape === "sector" && angle === null) {
    return <ErrorNotice>{strings.invalidAngle}</ErrorNotice>;
  }
  const result = computeArea(
    shape,
    {
      lengths: read.lengths,
      sides: sides ?? undefined,
      angle: angle ?? undefined,
    },
    { mark, style }
  );
  if (!result.ok) {
    return <ErrorNotice>{errorText(strings, result.error)}</ErrorNotice>;
  }
  const angleLabel =
    angleUnit === "deg" && angle?.degrees
      ? `θ = ${formatStyled(angle.degrees, style, mark)}°`
      : `θ = ${angle?.radianText ?? ""}`;
  return (
    <Results
      figure={
        <div className="space-y-2">
          <AreaDiagram
            angleDegrees={angle?.degreesValue ?? 60}
            angleLabel={angleLabel}
            labels={read.labels}
            shape={shape}
            sides={sides ?? 5}
            sidesLabel={`n = ${sides ?? ""}`}
            svgRef={svgRef}
            title={t(strings.figureTitle, { shape: strings.shapes[shape] })}
            values={read.numbers}
          />
          <DiagramDownload
            fileName={`area-${shape}`}
            label={strings.downloadFigure}
            svgRef={svgRef}
          />
        </div>
      }
      format={{ decimals: style === "decimal", mark }}
      places={places}
      result={result}
      setup={setup}
      shape={shape}
      strings={strings}
    />
  );
}

export default function AreaCalculatorWidget() {
  const strings = useToolUi(AREA_CALCULATOR_STRINGS);
  const [shape, setShape] = useState<AreaShapeId>("rectangle");
  const [values, setValues] = useState<Record<string, string>>(DEFAULT_VALUES);
  const [sidesText, setSidesText] = useState("5");
  const [angleText, setAngleText] = useState("60");
  const [angleUnit, setAngleUnit] = useState<AngleInputUnit>("deg");
  const [setup, setSetup] = useState<UnitSetup>({
    mixed: false,
    unit: "cm",
    units: {},
    resultUnit: "cm",
  });
  const [places, setPlaces] = useState<DecimalPlaces>("2");

  const fields = AREA_SHAPE_FIELDS[shape];
  const shownUnits = Object.fromEntries(
    fields.map((id) => [id, setup.units[id] ?? setup.unit])
  ) as Record<string, LengthUnitId>;

  return (
    <>
      <div className="space-y-4">
        <SelectField
          id={`${ID_PREFIX}-shape`}
          label={strings.shapeLabel}
          onChange={setShape}
          options={AREA_SHAPE_IDS.map((id) => ({
            value: id,
            label: strings.shapes[id],
          }))}
          value={shape}
        />
        <p className="text-muted-foreground text-xs">
          {strings.circleNote}{" "}
          <Link className="underline" href="/tools/circle-calculator">
            {strings.circleLink}
          </Link>
        </p>
        <MeasurementFields
          fields={fields.map((id) => ({ id, label: strings.fields[id] }))}
          idPrefix={ID_PREFIX}
          mixed={setup.mixed}
          onUnit={(id, unit) =>
            setSetup({ ...setup, units: { ...setup.units, [id]: unit } })
          }
          onValue={(id, value) => setValues({ ...values, [id]: value })}
          unitAria={strings.unitAria}
          units={shownUnits}
          values={values}
        />
        {shape === "polygon" ? (
          <TextField
            id={`${ID_PREFIX}-sides`}
            label={strings.sidesLabel}
            numeric
            onChange={setSidesText}
            value={sidesText}
          />
        ) : null}
        {shape === "sector" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              hint={angleUnit === "rad" ? strings.angleHint : undefined}
              id={`${ID_PREFIX}-angle`}
              label={strings.angleLabel}
              mono
              onChange={setAngleText}
              value={angleText}
            />
            <SelectField
              id={`${ID_PREFIX}-angle-unit`}
              label={strings.angleUnitLabel}
              onChange={(next) => {
                setAngleUnit(next);
                setAngleText(next === "deg" ? "60" : "pi/3");
              }}
              options={(["deg", "rad"] as const).map((id) => ({
                value: id,
                label: strings.angleUnits[id],
              }))}
              value={angleUnit}
            />
          </div>
        ) : null}
        <UnitControls
          idPrefix={ID_PREFIX}
          onPlaces={setPlaces}
          onSetup={setSetup}
          places={places}
          setup={setup}
          strings={strings}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          angleText={angleText}
          angleUnit={angleUnit}
          places={Number(places)}
          setup={setup}
          shape={shape}
          sidesText={sidesText}
          strings={strings}
          values={values}
        />
      </section>
    </>
  );
}
